export type HedgeMode = 'custom' | 'preset';
export type HedgePresetOptionSide = 'calls' | 'puts' | 'both';
export type HedgeExecutionSide = 'calls' | 'puts';
export interface VerticalPresetSettings {
    startMode: 'fixed' | 'straddlePercentage';
    startValue: number;
    sellMode: 'fixedWidth' | 'straddlePercentage';
    sellValue: number;
    minOffset?: number | null;
    maxOffset?: number | null;
    minWidth?: number | null;
    maxWidth?: number | null;
}
export interface HedgePresetSelection {
    mode?: HedgeMode;
    presetId?: string | null;
    presetValid?: boolean;
    presetName?: string | null;
}
export interface HedgePreset {
    id: string;
    underlying: string;
    name: string;
    optionSide: HedgePresetOptionSide;
    revision: number;
    nominalApgWidth: number;
    strategy: 'butterfly' | 'vertical';
    ivLevel: 'all';
    sizingMode: 'directWidth' | 'dynamicWidth';
    offset: number;
    offsetProportion?: number | null;
    minOffset?: number | null;
    requiresReconfiguration?: boolean;
    width?: number | null;
    straddleProportion?: number | null;
    floor?: number | null;
    verticalSettings?: VerticalPresetSettings | null;
}
export function validHedgePreset(p: HedgePreset): boolean {
    const positive = (v: any) => typeof v === 'number' && isFinite(v) && v > 0;
    const common = !!p && !!p.name && !!p.name.trim() && p.name.trim().length <= 200 && !!p.underlying
        && ['calls', 'puts', 'both'].indexOf(p.optionSide) >= 0
        && p.ivLevel === 'all' && positive(p.nominalApgWidth);
    if (!common) { return false; }
    if (p.strategy === 'vertical') {
        return validVerticalPreset(p.verticalSettings);
    }
    return p.strategy === 'butterfly'
        && typeof p.offset === 'number' && isFinite(p.offset)
        && (p.sizingMode === 'directWidth' ? positive(p.width)
            : p.sizingMode === 'dynamicWidth' && positive(p.straddleProportion)
                && typeof p.offsetProportion === 'number' && isFinite(p.offsetProportion)
                && typeof p.minOffset === 'number' && isFinite(p.minOffset) && p.minOffset >= 0
                && validHedgePresetFloor(p.floor));
}
export function validVerticalPreset(v: VerticalPresetSettings): boolean {
    const nonNegative = (n: any) => typeof n === 'number' && isFinite(n) && n >= 0;
    const bounds = (min: number, max: number) => (min == null || nonNegative(min))
        && (max == null || nonNegative(max) && max > 0) && (min == null || max == null || min <= max);
    return !!v && ['fixed', 'straddlePercentage'].indexOf(v.startMode) >= 0
        && ['fixedWidth', 'straddlePercentage'].indexOf(v.sellMode) >= 0
        && (v.startMode === 'fixed') === (v.sellMode === 'fixedWidth')
        && nonNegative(v.startValue) && nonNegative(v.sellValue) && v.sellValue > 0
        && (v.startMode === 'fixed' || bounds(v.minOffset, v.maxOffset))
        && (v.sellMode === 'fixedWidth' || bounds(v.minWidth, v.maxWidth))
        && (v.startMode !== 'straddlePercentage' || v.sellMode !== 'straddlePercentage' || v.sellValue > v.startValue);
}
export function cloneHedgePreset(p: HedgePreset): HedgePreset {
    return p.verticalSettings ? {...p, verticalSettings: {...p.verticalSettings}} : {...p};
}
export function validHedgePresetFloor(value: number): boolean {
    return typeof value === 'number' && isFinite(value) && value > 0 && value % 10 === 0;
}
export function eligibleHedgePresets(rows: HedgePreset[], side: HedgeExecutionSide): HedgePreset[] {
    if (side !== 'calls' && side !== 'puts') { return []; }
    const ordinal = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0;
    return rows.filter(p => p.optionSide === side || p.optionSide === 'both').sort((a, b) =>
        ordinal(a.name.toUpperCase(), b.name.toUpperCase()) || ordinal(a.name, b.name) || ordinal(a.id, b.id));
}
export function presetPayload(p: HedgePreset): HedgePreset {
    const result = {...cloneHedgePreset(p), name: p.name.trim()};
    result.requiresReconfiguration = false;
    if (result.strategy === 'vertical') {
        result.sizingMode = null; result.offset = 0; result.width = null;
        result.straddleProportion = null; result.floor = null;
        result.offsetProportion = result.minOffset = null;
        const v = result.verticalSettings;
        if (v.startMode === 'fixed') { v.minOffset = v.maxOffset = null; }
        if (v.sellMode === 'fixedWidth') { v.minWidth = v.maxWidth = null; }
    } else {
        result.verticalSettings = null;
        if (result.sizingMode === 'directWidth') {
            result.straddleProportion = result.floor = result.offsetProportion = result.minOffset = null;
        } else { result.offset = 0; result.width = null; }
    }
    return result;
}
export function isPresetSelectionComplete(s: HedgePresetSelection): boolean {
    return !!s && !!s.presetId && s.presetValid === true;
}
